import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import ExplicitTag from '../../../Components/GeneralComponents/ExplicitTag'
import classes from './DraggableList.module.css'

function SortableListItem({ collection, index, onIdChange, onRemove, isDuplicate }) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedId, setEditedId] = useState(collection.f_id)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: collection.f_id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const handleBlur = async () => {
    setIsEditing(false)
    if (editedId !== collection.f_id && editedId.trim()) {
      const success = await onIdChange(index, editedId.trim())
      if (!success) {
        setEditedId(collection.f_id)
      }
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur()
    }
    if (e.key === 'Escape') {
      setEditedId(collection.f_id)
      setIsEditing(false)
    }
  }

  return (
    <div ref={setNodeRef} style={style} className={classes.listItem}>
      <div className={classes.dragHandle} {...attributes} {...listeners}>
        <span className={classes.dragIcon}>&#8942;&#8942;</span>
      </div>
      <span className={classes.listIndex}>{index + 1}</span>
      {isDuplicate && <span className={classes.dupeBadge}>DUPE</span>}
      <button
        className={classes.openButton}
        onClick={() => window.open(`/collections/${collection.f_id}`, '_blank')}
        title="Open collection"
      >
        &#8599;
      </button>
      <span className={classes.listName}>
        {collection.name || 'Untitled'}
        {collection.is_explicit && <span style={{ marginLeft: '5px' }}><ExplicitTag size="xsmall" /></span>}
      </span>
      <input
        type="text"
        value={isEditing ? editedId : collection.f_id}
        onChange={(e) => {
          setEditedId(e.target.value)
          setIsEditing(true)
        }}
        onFocus={() => setIsEditing(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={classes.listInput}
        placeholder="Collection ID"
      />
      <button
        className={classes.removeButton}
        onClick={() => onRemove(index)}
        title="Remove collection"
      >
        &times;
      </button>
    </div>
  )
}

SortableListItem.propTypes = {
  collection: PropTypes.shape({
    f_id: PropTypes.string.isRequired,
    name: PropTypes.string,
    is_explicit: PropTypes.bool,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onIdChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  isDuplicate: PropTypes.bool,
}

export default function DraggableList({ collections, onReorder, onIdChange, onAddCollection, onRemoveCollection }) {
  const [activeId, setActiveId] = useState(null)
  const [newId, setNewId] = useState('')
  const [isAdding, setIsAdding] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragStart = (event) => {
    setActiveId(event.active.id)
  }

  const handleDragEnd = (event) => {
    const { active, over } = event
    setActiveId(null)

    if (over && active.id !== over.id) {
      const oldIndex = collections.findIndex((c) => c.f_id === active.id)
      const newIndex = collections.findIndex((c) => c.f_id === over.id)

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(collections, oldIndex, newIndex)
        onReorder(newOrder)
      }
    }
  }

  const handleDragCancel = () => {
    setActiveId(null)
  }

  const handleAddSubmit = async () => {
    if (!newId.trim()) return
    setIsAdding(true)
    const success = await onAddCollection(newId.trim())
    if (success) {
      setNewId('')
    }
    setIsAdding(false)
  }

  const handleAddKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleAddSubmit()
    }
  }

  const activeCollection = collections.find((c) => c.f_id === activeId)

  const idCounts = collections.reduce((acc, c) => {
    acc[c.f_id] = (acc[c.f_id] || 0) + 1
    return acc
  }, {})
  const isDuplicate = (fId) => idCounts[fId] > 1

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        items={collections.map((c) => c.f_id)}
        strategy={verticalListSortingStrategy}
      >
        <div className={classes.listContainer}>
          {collections.map((collection, index) => (
            <SortableListItem
              key={`${collection.f_id}-${index}`}
              collection={collection}
              index={index}
              onIdChange={onIdChange}
              onRemove={onRemoveCollection}
              isDuplicate={isDuplicate(collection.f_id)}
            />
          ))}
          <div className={classes.addRow}>
            <span className={classes.addIcon}>+</span>
            <input
              type="text"
              value={newId}
              onChange={(e) => setNewId(e.target.value)}
              onKeyDown={handleAddKeyDown}
              className={classes.addInput}
              placeholder="Enter collection ID to add"
              disabled={isAdding}
            />
            <button
              className={classes.addButton}
              onClick={handleAddSubmit}
              disabled={isAdding || !newId.trim()}
            >
              {isAdding ? '...' : 'ADD'}
            </button>
          </div>
        </div>
      </SortableContext>

      <DragOverlay>
        {activeCollection ? (
          <div className={classes.dragOverlay}>
            <span className={classes.dragHandle}>
              <span className={classes.dragIcon}>&#8942;&#8942;</span>
            </span>
            <span className={classes.listIndex}>
              {collections.findIndex((c) => c.f_id === activeId) + 1}
            </span>
            <span className={classes.listName}>{activeCollection.name || 'Untitled'}</span>
            <span className={classes.listInput}>{activeCollection.f_id}</span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

DraggableList.propTypes = {
  collections: PropTypes.arrayOf(
    PropTypes.shape({
      f_id: PropTypes.string.isRequired,
      name: PropTypes.string,
    })
  ).isRequired,
  onReorder: PropTypes.func.isRequired,
  onIdChange: PropTypes.func.isRequired,
  onAddCollection: PropTypes.func.isRequired,
  onRemoveCollection: PropTypes.func.isRequired,
}
